import { gql, useQuery } from '@apollo/client';
import { Stack } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import * as React from 'react';

import { FeaturedSponsor } from '@/components/FeaturedSponsor';
import { MembershipRenewalModal } from '@/components/MembershipRenewalModal';
import { MembershipSupportingContent } from '@/components/MembershipSupportingContent';
import { BlogDetail, Post, QuestionDetail } from '@/components/Post';
import { UpcomingEvents } from '@/components/UpcomingEvents';
import { usePermissions } from '@/hooks/usePermissions';
import { Loading } from '@/imports/ui/chakra/components/Loading';
import { OnboardingChecklist } from '@/imports/ui/components/OnboardingChecklist';
import { useAutoLikeComment } from '@/imports/ui/hooks/useAutoLikeComment';
import { useAutoLikePost } from '@/imports/ui/hooks/useAutoLikePost';
import { useAutoPollVote } from '@/imports/ui/hooks/useAutoPollVote';
import { Vanilla, VanillaAside, VanillaMain } from '@/layouts/Vanilla';

import {
  CommunityPostPage_GetPost,
  CommunityPostPage_GetPostVariables,
  CommunityPostPage_GetPost_postV2_result,
} from './graphql-types/CommunityPostPage_GetPost';

import { PostRemovedCard } from './components/PostRemovedCard';
import { SeeMoreButtons } from './components/SeeMoreButtons';

const GET_POST = gql`
  query CommunityPostPage_GetPost($input: SinglePostInputV2!) {
    postV2(input: $input) {
      result {
        _id
        group {
          _id
        }
        ...Post_PostV2Fragment
        ...BlogDetail_BlogFragment
        ...QuestionDetail_QuestionV2Fragment
        ...SeeMoreButtons_PostV2Fragment
      }
    }
  }
  ${Post.postFragment}
  ${BlogDetail.blogFragment}
  ${QuestionDetail.questionFragment}
  ${SeeMoreButtons.postFragment}
`;

const MainContent: React.FC<{
  post: CommunityPostPage_GetPost_postV2_result;
}> = ({ post }) => {
  useAutoPollVote(VALID_ROUTES);
  useAutoLikeComment(VALID_ROUTES);
  useAutoLikePost(VALID_ROUTES);

  return (
    <Stack spacing="10">
      {post.__typename === 'BlogV2' ? (
        <BlogDetail blog={post} options={{ isExpandedByDefault: true }} />
      ) : post.__typename === 'QuestionV2' ? (
        <QuestionDetail
          question={post}
          options={{ isExpandedByDefault: true }}
        />
      ) : (
        <Post post={post} options={{ isExpandedByDefault: true }} />
      )}

      <SeeMoreButtons post={post} />
    </Stack>
  );
};

const VALID_ROUTES = {
  validRoutes: ['community/post'],
};

export const CommunityPostPage: React.FC = () => {
  const permissions = usePermissions();
  const router = useRouter();

  const query = useQuery<
    CommunityPostPage_GetPost,
    CommunityPostPage_GetPostVariables
  >(GET_POST, {
    variables: {
      input: {
        id: router.query.postId.toString(),
      },
    },
  });

  // if it has a group then it is part of the community. if not we redirect
  const shouldRedirect =
    query.data?.postV2.result && !query.data.postV2.result.group;

  React.useEffect(() => {
    if (
      !permissions.isLoading &&
      !query.loading &&
      !permissions.isAuthenticated &&
      (!query.data || !query.data.postV2.result || query.error)
    ) {
      router.push({
        pathname: `/login`,
        query: {
          redirect: router.asPath,
        },
      });
    } else if (shouldRedirect) {
      router.replace({
        pathname: '/post/[postId]',
        query: router.query,
      });
    }
  }, [
    permissions.isLoading,
    query.loading,
    router,
    permissions.isAuthenticated,
    query.data,
    query.error,
    shouldRedirect,
  ]);

  return (
    <Vanilla>
      <VanillaMain>
        {query.loading || shouldRedirect ? (
          <Loading />
        ) : !query.data || !query.data.postV2.result || query.error ? (
          <PostRemovedCard />
        ) : (
          <>
            <MainContent post={query.data.postV2.result} />
            <MembershipRenewalModal />
          </>
        )}
      </VanillaMain>
      <VanillaAside>
        <Stack spacing="4">
          <OnboardingChecklist />
          <MembershipSupportingContent />
          <FeaturedSponsor />
          {permissions.canAccessEventsFeed && <UpcomingEvents />}
        </Stack>
      </VanillaAside>
    </Vanilla>
  );
};
